import { useState, useCallback } from 'react';

/**
 * useLocalStorageTTL
 * This hook is used to manage local storage with a time-to-live (TTL) feature.
 * It allows you to set a value in local storage with an expiration time.
 *
 * @example
 * ```tsx
 * const [data, setData, clearData] = useLocalStorageTTL('key', 1000 * 60 * 60, 'default value');
 * ```
 *
 * @param key - The key under which the value is stored in local storage.
 * @param time - The time-to-live (TTL) in milliseconds. After this time, the value will be removed from local storage.
 * @param defaultValue - The default value to be used if the key does not exist in local storage or if the value has expired.
 */
export const useLocalStorageTTL = <T>(
  key: string | undefined,
  time: number = 1000 * 60 * 60, // Default to 1 hour
  defaultValue?: T,
): [T, (value: T) => void, () => void] => {
  const [storedValue] = useState<T>(() => {
    if (!key) return defaultValue as T;

    const item = window.localStorage.getItem(key);
    if (item) {
      const { value, expiry } = JSON.parse(item);
      if (expiry > Date.now()) {
        return value;
      }
      window.localStorage.removeItem(key);
    }
    return defaultValue as T;
  });

  const set = useCallback(
    (value: T) => {
      if (!key) return;

      if (value === storedValue) {
        return;
      }

      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      const expiry = Date.now() + time;

      window.localStorage.setItem(
        key,
        JSON.stringify({ value: valueToStore, expiry }),
      );
    },
    [key, storedValue, time],
  );

  const remove = useCallback(() => {
    if (!key) return;

    window.localStorage.removeItem(key);
  }, [key, defaultValue]);

  return [storedValue, set, remove];
};
